import {
  DeleteIcon,
  LogoutIcon,
  UserPlusIcon,
  EditIcon,
  FolderIcon,
  InfoIcon,
  LocationIcon,
  LockIcon,
  UserIcon,
  UsersIcon,
  WarningIcon,
} from '../../components/ui/icons';
import { GoogleLogo } from '../../components/ui/GoogleLogo';
import { DialogContent } from '../../components/ui/DialogContent';
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CollectionRole,
  InventoryAuthority,
  GpsCoordinates,
} from '@qkindex/contracts';
import BinModal from '../../components/BinModal';
import {
  NavigationMenuList,
  NavigationMenuRow,
} from '../../components/ui/NavigationMenuRow';
import { MoreSheet } from '../../components/ui/MoreSheet';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { DialogActions } from '../../components/ui/DialogActions';
import { CollectionSwitcherSheet } from '../../components/ui/CollectionSwitcherSheet';
import { LoadingState } from '../../components/ui/States';
import { InventoryDensityControl } from '../../components/ui/InventoryDensityControl';
import { AccountSection } from '../settings/AccountSection';
import { BillingSection } from '../billing/BillingSection';
import { CollectionAiSettings } from '../collection/components/CollectionAiSettings';
import { CollectionRolesSection } from '../collection/components/CollectionRolesSection';
import { CollectionPeopleSection } from '../collection/components/CollectionPeopleSection';
import { CollectionTransferSection } from '../collection/components/CollectionTransferSection';
import { CollectionDeletionSection } from '../collection/components/CollectionDeletionSection';
import { CollectionLeaveSection } from '../collection/components/CollectionLeaveSection';
import { SettingsControllers } from '../settings/types';
import { CollectionActivitySection } from '../collection/components/CollectionActivitySection';
import { CollectionGpsDialog } from '../collection/components/CollectionGpsDialog';
import { CollectionNameDialog } from '../../components/ui/CollectionSwitcherSheet';
import { MenuPresentation } from '../../components/ui/MenuPresentation';

import { SharedWithMe } from '../sharing/SharedViews';
import { CollectionSharingSection } from '../sharing/CollectionSharingSection';
import { getRemainingCooldownDays } from '../../services/usernameService';
import { ChangeUsernameModal } from '../settings/ChangeUsernameModal';

export type AppSurface =
  | 'collections'
  | 'bin'
  | 'appearance'
  | 'account'
  | 'sharing'
  | 'shared-with-me'
  | 'collection'
  | 'general'
  | 'activity'
  | 'people'
  | 'roles'
  | 'danger'
  | 'transfer'
  | 'leave'
  | 'deletion'
  | 'billing'
  | 'buy-credits'
  | 'transactions';
export type AppChildWorkflow = 'rename' | 'password' | 'username' | 'gps';

interface BaseProps {
  surface: AppSurface | null;
  onClose: () => void;
  onBack?: () => void;
  onNavigate: (surface: AppSurface) => void;
}

interface WorkflowProps extends BaseProps {
  onSharingPendingChange?: (pending: boolean) => void;
  collectionSelector?: React.ComponentProps<typeof CollectionSwitcherSheet>;
  sessionReady?: boolean;
  collectionTransition?: 'loading' | 'unavailable';
  menu?: React.ComponentProps<typeof MoreSheet>;
  bin?: React.ComponentProps<typeof BinModal>;
  cardSize: React.ComponentProps<typeof InventoryDensityControl>['cardSize'];
  onCardSizeChange: (
    value: React.ComponentProps<typeof InventoryDensityControl>['cardSize'],
  ) => void;
  removeMargins: boolean;
  onRemoveMarginsChange: (value: boolean) => void;
  account: SettingsControllers['account'];
  onSignOut: () => void;
  ownerAuthority?: InventoryAuthority;
  inventoryAdminAuthority?: InventoryAuthority;
  canRenameCollection?: boolean;
  inventoryAuthority?: InventoryAuthority;
  readOnly: boolean;
  canManageCollection: boolean;
  collectionActionsAvailable: boolean;
  collectionName?: string;
  collectionRole?: CollectionRole;
  collectionLifecycle?: string;
  collectionGps?: GpsCoordinates;
  onUpdateCollectionGps?: (gps: GpsCoordinates | null) => Promise<void>;
  onRenameCollection?: (name: string) => Promise<void>;
  childWorkflow: AppChildWorkflow | null;
  onOpenChild: (child: AppChildWorkflow) => void;
  onCloseChild: () => void;
  peopleCollectionId?: string;
  activityCollectionId?: string;
  membershipVersion?: number;
  purchaseReturn?: 'returned' | 'cancelled';
  leaveCollection?: React.ComponentProps<typeof CollectionLeaveSection>;
  transferCollection?: React.ComponentProps<typeof CollectionTransferSection>;
  deletionCollection?: React.ComponentProps<typeof CollectionDeletionSection>;
}

function SurfaceFrame({
  title,
  children,
  onClose,
  onBack,
  actions,
  id,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  onBack?: () => void;
  actions?: React.ReactNode;
  id: string;
}) {
  return (
    <>
      <DialogHeader id={id} title={title} onClose={onClose} onBack={onBack} />
      <DialogContent>{children}</DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </>
  );
}

function AppearanceSurface({ props }: { props: WorkflowProps }) {
  return (
    <SurfaceFrame
      title="Appearance"
      id="appearance-surface-title"
      onClose={props.onClose}
      onBack={props.onBack}
      actions={
        <Button variant="contained" onClick={props.onClose}>
          Done
        </Button>
      }
    >
      <InventoryDensityControl
        cardSize={props.cardSize}
        onCardSizeChange={props.onCardSizeChange}
        removeMargins={props.removeMargins}
        onRemoveMarginsChange={props.onRemoveMarginsChange}
      />
    </SurfaceFrame>
  );
}

function AccountSurface({ props }: { props: WorkflowProps }) {
  const [connecting, setConnecting] = useState(false);
  const passwordOpen = props.childWorkflow === 'password';
  const usernameOpen = props.childWorkflow === 'username';
  const openPassword = () => props.onOpenChild('password');
  const openUsername = () => props.onOpenChild('username');
  const remainingCooldownDays = getRemainingCooldownDays(
    props.account.usernameUpdatedAt,
  );
  const hasPassword = props.account.providerIds.includes('password');
  const hasGoogle = props.account.providerIds.includes('google.com');
  const connect = async () => {
    setConnecting(true);
    try {
      await props.account.connectGoogle();
    } finally {
      setConnecting(false);
    }
  };
  if (passwordOpen && hasPassword)
    return (
      <AccountSection
        embedded
        open
        changePassword={props.account.changePassword}
        onClose={props.onCloseChild}
        onDismiss={props.onClose}
      />
    );
  if (usernameOpen && props.account.userId)
    return (
      <ChangeUsernameModal
        embedded
        open
        onClose={props.onCloseChild}
        onDismiss={props.onClose}
        userId={props.account.userId}
        usernameUpdatedAt={props.account.usernameUpdatedAt}
        onUsernameChanged={props.account.onUsernameChanged || (() => {})}
      />
    );
  return (
    <SurfaceFrame
      title="Account"
      id="account-surface-title"
      onClose={props.onClose}
      onBack={props.onBack}
    >
      <Stack spacing={1}>
        {(props.account.username || props.account.userId) && (
          <SettingsDetailRow
            icon={<UserIcon />}
            label="Username"
            value={
              props.account.username ? `@${props.account.username}` : 'Not set'
            }
            actionLabel="Change username"
            onClick={props.account.userId ? openUsername : undefined}
            disabled={remainingCooldownDays > 0}
            disabledReason={`You can change your username again in ${remainingCooldownDays} day${remainingCooldownDays === 1 ? '' : 's'}.`}
          />
        )}
        {hasPassword && (
          <Button
            startIcon={<LockIcon />}
            onClick={openPassword}
            sx={{ justifyContent: 'flex-start' }}
          >
            Change password
          </Button>
        )}
        {!hasGoogle && (
          <Button
            startIcon={<GoogleLogo />}
            onClick={() => {
              void connect();
            }}
            disabled={connecting}
            sx={{ justifyContent: 'flex-start' }}
          >
            {connecting ? 'Connecting Google…' : 'Connect Google account'}
          </Button>
        )}
        {hasGoogle && (
          <Button
            startIcon={<GoogleLogo />}
            disabled
            sx={{ justifyContent: 'flex-start' }}
          >
            Google connected
          </Button>
        )}
      </Stack>
    </SurfaceFrame>
  );
}

function SettingsDetailRow({
  icon,
  label,
  value,
  actionLabel,
  onClick,
  disabled = false,
  disabledReason,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  actionLabel?: string;
  onClick?: () => void;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const content = (
    <>
      {icon}
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="span"
          variant="body2"
          sx={{ display: 'block', fontWeight: 600, color: 'primary.main' }}
        >
          {label}
        </Typography>
        <Typography
          component="span"
          variant="body1"
          sx={{
            display: 'block',
            mt: 0.5,
            color: 'text.primary',
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </Typography>
      </Box>
      {onClick &&
        (disabled ? (
          <Tooltip title={disabledReason} describeChild>
            <Box
              component="span"
              tabIndex={0}
              aria-label={actionLabel}
              sx={{ display: 'inline-flex' }}
            >
              <IconButton
                disabled
                aria-label={actionLabel}
                sx={{ color: 'primary.main' }}
              >
                <EditIcon />
              </IconButton>
            </Box>
          </Tooltip>
        ) : (
          <EditIcon />
        ))}
    </>
  );
  const sx = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    px: 1,
    py: 1.5,
    textAlign: 'left' as const,
    color: 'primary.main',
  };
  return onClick && !disabled ? (
    <Button fullWidth aria-label={actionLabel} onClick={onClick} sx={sx}>
      {content}
    </Button>
  ) : (
    <Box sx={sx}>{content}</Box>
  );
}

function GeneralSurface({ props }: { props: WorkflowProps }) {
  const canEditGps =
    props.collectionRole === 'owner' &&
    props.collectionLifecycle === 'active' &&
    Boolean(props.onUpdateCollectionGps);
  const gpsOpen = props.childWorkflow === 'gps';
  const canRename =
    (props.canRenameCollection ?? props.canManageCollection) &&
    Boolean(props.onRenameCollection);
  const renameOpen = props.childWorkflow === 'rename';
  const openRename = () => props.onOpenChild('rename');
  const closeRename = props.onCloseChild;
  return (
    <>
      <SurfaceFrame
        title="General"
        id="collection-general-title"
        onClose={props.onClose}
        onBack={props.onBack}
      >
        <Stack spacing={1}>
          <SettingsDetailRow
            icon={<FolderIcon />}
            label="Collection name"
            value={
              <bdi dir="auto">
                {props.collectionName || 'Active collection'}
              </bdi>
            }
            actionLabel="Rename collection"
            onClick={canRename ? openRename : undefined}
          />
          {canEditGps && (
            <SettingsDetailRow
              icon={<LocationIcon />}
              label="GPS location"
              value={
                props.collectionGps
                  ? `${props.collectionGps.latitude}, ${props.collectionGps.longitude}`
                  : 'Not set'
              }
              actionLabel="Edit GPS location"
              onClick={() => props.onOpenChild('gps')}
            />
          )}
          {props.collectionLifecycle &&
            props.collectionLifecycle !== 'active' && (
              <Typography variant="body2" color="text.secondary">
                Lifecycle: {props.collectionLifecycle.replace('-', ' ')}
              </Typography>
            )}
        </Stack>
      </SurfaceFrame>
      {canEditGps && (
        <CollectionGpsDialog
          open={gpsOpen}
          collectionName={props.collectionName || 'Active collection'}
          gps={props.collectionGps}
          onClose={props.onCloseChild}
          onSubmit={props.onUpdateCollectionGps!}
        />
      )}
      {canRename && (
        <CollectionNameDialog
          open={renameOpen}
          title="Rename collection"
          action="Rename"
          onClose={closeRename}
          onSubmit={props.onRenameCollection!}
        />
      )}
    </>
  );
}

function CollectionSurface({ props }: { props: WorkflowProps }) {
  return (
    <>
      <DialogHeader
        title="Collection settings"
        id="manage-collection-title"
        onClose={props.onClose}
        onBack={props.onBack}
      />
      <NavigationMenuList label="Collection settings">
        {props.collectionActionsAvailable && (
          <NavigationMenuRow
            icon={<InfoIcon />}
            label="General"
            onClick={() => props.onNavigate('general')}
          />
        )}
        {props.peopleCollectionId && (
          <NavigationMenuRow
            icon={<UsersIcon />}
            label="People"
            onClick={() => props.onNavigate('people')}
          />
        )}
        {props.peopleCollectionId && (
          <NavigationMenuRow
            icon={<UsersIcon />}
            label="Tags and sharing"
            description="Share tagged items with people or links"
            onClick={() => props.onNavigate('sharing')}
          />
        )}
        {props.activityCollectionId && (
          <NavigationMenuRow
            icon={<InfoIcon />}
            label="Activity"
            onClick={() => props.onNavigate('activity')}
          />
        )}
        {(props.transferCollection ||
          props.leaveCollection ||
          props.deletionCollection) && (
          <NavigationMenuRow
            icon={<WarningIcon />}
            label="Danger zone"
            description="Transfer, leave, or delete this collection"
            onClick={() => props.onNavigate('danger')}
            tone="danger"
          />
        )}
      </NavigationMenuList>
    </>
  );
}

function ChildSurface({ props }: { props: WorkflowProps }) {
  switch (props.surface) {
    case 'sharing':
      return props.peopleCollectionId ? (
        <CollectionSharingSection
          key={props.peopleCollectionId}
          collectionId={props.peopleCollectionId}
          userId={props.account.userId || ''}
          onClose={props.onClose}
          onBack={props.onBack}
          onPendingChange={props.onSharingPendingChange}
        />
      ) : null;
    case 'activity':
      return props.activityCollectionId ? (
        <SurfaceFrame
          title="Activity"
          id="activity-title"
          onClose={props.onClose}
          onBack={props.onBack}
        >
          <CollectionActivitySection
            key={`${props.account.userId}:${props.activityCollectionId}:${props.collectionRole}`}
            collectionId={props.activityCollectionId}
            userId={props.account.userId}
            membershipVersion={props.membershipVersion}
          />
        </SurfaceFrame>
      ) : null;
    case 'people':
      return props.peopleCollectionId &&
        props.collectionRole &&
        props.account.userId ? (
        <SurfaceFrame
          title="People"
          id="people-title"
          onClose={props.onClose}
          onBack={props.onBack}
        >
          <Stack spacing={2}>
            {props.collectionRole === 'owner' && props.ownerAuthority && (
              <Box
                sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, px: 1 }}
              >
                <CollectionAiSettings
                  collectionId={props.ownerAuthority.collectionId}
                  role={props.collectionRole}
                />
              </Box>
            )}
            <CollectionPeopleSection
              collectionId={props.peopleCollectionId}
              actorRole={props.collectionRole}
              actorUserId={props.account.userId}
              onRoles={() => props.onNavigate('roles')}
            />
          </Stack>
        </SurfaceFrame>
      ) : null;
    case 'roles':
      return props.peopleCollectionId ? (
        <SurfaceFrame
          title="Roles and permissions"
          id="roles-title"
          onClose={props.onClose}
          onBack={props.onBack}
        >
          <CollectionRolesSection />
        </SurfaceFrame>
      ) : null;
    case 'danger':
      return (
        <>
          <DialogHeader
            title="Danger zone"
            id="danger-title"
            onClose={props.onClose}
            onBack={props.onBack}
          />
          <NavigationMenuList label="Danger zone actions">
            {props.transferCollection && (
              <NavigationMenuRow
                icon={<UserPlusIcon />}
                label="Transfer ownership"
                onClick={() => props.onNavigate('transfer')}
              />
            )}
            {props.leaveCollection && (
              <NavigationMenuRow
                icon={<LogoutIcon />}
                label="Leave collection"
                onClick={() => props.onNavigate('leave')}
              />
            )}
            {props.deletionCollection && (
              <NavigationMenuRow
                icon={<DeleteIcon />}
                label="Delete collection"
                onClick={() => props.onNavigate('deletion')}
                tone="danger"
              />
            )}
          </NavigationMenuList>
        </>
      );
    case 'transfer':
      return props.transferCollection ? (
        <>
          <DialogHeader
            title="Transfer ownership"
            id="transfer-title"
            onClose={props.onClose}
            onBack={props.onBack}
          />
          <CollectionTransferSection
            {...props.transferCollection}
            collectionName={props.collectionName}
            onCancel={props.onClose}
          />
        </>
      ) : null;
    case 'leave':
      return props.leaveCollection ? (
        <>
          <DialogHeader
            title="Leave collection"
            id="leave-title"
            onClose={props.onClose}
            onBack={props.onBack}
          />
          <CollectionLeaveSection
            {...props.leaveCollection}
            collectionName={props.collectionName}
            onCancel={props.onClose}
          />
        </>
      ) : null;
    case 'deletion':
      return props.deletionCollection ? (
        <>
          <DialogHeader
            title="Delete collection"
            id="deletion-title"
            onClose={props.onClose}
            onBack={props.onBack}
          />
          <CollectionDeletionSection
            {...props.deletionCollection}
            onCancel={props.onClose}
          />
        </>
      ) : null;
    default:
      return null;
  }
}

function SurfaceContent(props: WorkflowProps) {
  if (!props.surface) return null;
  if (props.surface === 'shared-with-me')
    return (
      <SurfaceFrame
        title="Shared with me"
        id="shared-with-me-title"
        onClose={props.onClose}
        onBack={props.onBack}
      >
        <SharedWithMe />
      </SurfaceFrame>
    );
  if (props.surface === 'appearance')
    return <AppearanceSurface props={props} />;
  if (props.surface === 'account') return <AccountSurface props={props} />;
  if (props.surface === 'collection')
    return <CollectionSurface props={props} />;
  if (props.surface === 'general') return <GeneralSurface props={props} />;
  if (
    props.surface === 'billing' ||
    props.surface === 'buy-credits' ||
    props.surface === 'transactions'
  ) {
    const title =
      props.surface === 'buy-credits'
        ? 'Buy credits'
        : props.surface === 'transactions'
          ? 'Transactions'
          : 'Billing';
    if (props.surface === 'buy-credits') {
      return (
        <>
          <DialogHeader
            id={surfaceTitleIds[props.surface]}
            title={title}
            onClose={props.onClose}
            onBack={props.onBack}
          />
          <BillingSection
            key={props.account.userId}
            userId={props.account.userId}
            purchaseReturn={props.purchaseReturn}
            purchaseDialog
          />
        </>
      );
    }
    return (
      <SurfaceFrame
        title={title}
        id={surfaceTitleIds[props.surface]}
        onClose={props.onClose}
        onBack={props.onBack}
      >
        <BillingSection
          key={props.account.userId}
          userId={props.account.userId}
          purchaseReturn={props.purchaseReturn}
        />
      </SurfaceFrame>
    );
  }
  return <ChildSurface props={props} />;
}

const surfaceTitleIds: Record<AppSurface, string> = {
  sharing: 'sharing-title',
  'shared-with-me': 'shared-with-me-title',
  collections: 'collection-switcher-title',
  bin: 'bin-title',
  appearance: 'appearance-surface-title',
  account: 'account-surface-title',
  collection: 'manage-collection-title',
  general: 'collection-general-title',
  activity: 'activity-title',
  people: 'people-title',
  roles: 'roles-title',
  danger: 'danger-title',
  transfer: 'transfer-title',
  leave: 'leave-title',
  deletion: 'deletion-title',
  billing: 'billing-title',
  'buy-credits': 'buy-credits-title',
  transactions: 'transactions-title',
};

export function AppWorkflowSurfaces(props: WorkflowProps) {
  const activeView = props.collectionTransition
    ? 'collection-transition'
    : props.sessionReady === false && props.surface !== 'collections'
      ? null
      : props.bin?.open
        ? 'bin'
        : props.surface || (props.menu?.open ? 'more' : null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [sharingPending, setSharingPending] = useState(false);
  const lastPresentation = useRef<{
    view: typeof activeView;
    props: WorkflowProps;
  } | null>(null);
  useEffect(() => {
    if (props.sessionReady === false) lastPresentation.current = null;
    else if (activeView) lastPresentation.current = { view: activeView, props };
  }, [activeView, props]);
  // Preserve the outgoing content until the dialog's closing transition ends.
  const presentation = activeView
    ? { view: activeView, props }
    : props.sessionReady === false
      ? null
      : lastPresentation.current;
  const displayedView = presentation?.view;
  const displayedProps = presentation?.props || props;
  useEffect(() => {
    // Navigation removes the focused menu button; focus the replacement content
    // without restarting the modal's focus trap or opening transition.
    if (activeView) contentRef.current?.focus();
  }, [activeView, props.childWorkflow]);
  const onClose =
    activeView === 'bin'
      ? props.bin!.onClose
      : activeView === 'collections'
        ? props.collectionSelector!.onClose
        : activeView === 'more'
          ? props.menu!.onClose
          : props.onClose;
  const titleId =
    displayedView === 'bin'
      ? 'bin-title'
      : displayedView === 'collection-transition'
        ? 'collection-transition-title'
        : displayedView === 'more'
          ? 'more-sheet-title'
          : displayedProps.surface === 'account' &&
              displayedProps.childWorkflow === 'password'
            ? 'change-password-title'
            : displayedProps.surface === 'account' &&
                displayedProps.childWorkflow === 'username'
              ? 'change-username-title'
              : displayedProps.surface
                ? surfaceTitleIds[displayedProps.surface]
                : undefined;
  return (
    <MenuPresentation
      open={Boolean(activeView)}
      onClose={() => {
        if (displayedProps.surface !== 'sharing' || !sharingPending) onClose();
      }}
      titleId={titleId}
    >
      <Box
        key={displayedView}
        ref={contentRef}
        tabIndex={-1}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        {displayedView === 'collection-transition' ? (
          <SurfaceFrame
            title="Collection settings"
            id="collection-transition-title"
            onClose={props.onClose}
            onBack={props.onBack}
          >
            {props.collectionTransition === 'unavailable' ? (
              <Typography role="alert">
                This collection could not be opened. Go back to choose another
                collection.
              </Typography>
            ) : (
              <LoadingState label="Loading collection…" />
            )}
          </SurfaceFrame>
        ) : displayedView === 'collections' &&
          displayedProps.collectionSelector ? (
          <CollectionSwitcherSheet
            {...displayedProps.collectionSelector}
            embedded
          />
        ) : displayedView === 'bin' ? (
          <BinModal
            {...displayedProps.bin!}
            open
            onBack={displayedProps.onBack}
            embedded
          />
        ) : displayedView === 'more' ? (
          <MoreSheet
            {...displayedProps.menu!}
            showBilling={Boolean(displayedProps.account.userId)}
            onSignOut={
              displayedProps.account.userId
                ? displayedProps.onSignOut
                : undefined
            }
            embedded
          />
        ) : displayedView ? (
          <SurfaceContent
            {...displayedProps}
            onSharingPendingChange={setSharingPending}
          />
        ) : null}
      </Box>
    </MenuPresentation>
  );
}
